import { useState, useCallback, useRef, useEffect } from 'react'
import { useSearchAddresses, AddressSuggestion } from '../../api/addresses'
import styles from './AddressSearch.module.css'

interface AddressSearchProps {
  value: string
  onChange: (value: string) => void
  onSelect: (suggestion: AddressSuggestion) => void
  placeholder?: string
}

export function AddressSearch({
  value,
  onChange,
  onSelect,
  placeholder = 'Search address...',
}: AddressSearchProps) {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (value.length >= 3) {
        setDebouncedQuery(value)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [value])

  const { data: suggestions, isLoading } = useSearchAddresses(debouncedQuery, debouncedQuery.length >= 3)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = useCallback((suggestion: AddressSuggestion) => {
    onChange(suggestion.displayName)
    onSelect(suggestion)
    setShowSuggestions(false)
  }, [onChange, onSelect])

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setShowSuggestions(true)
        }}
        onFocus={() => setShowSuggestions(true)}
        placeholder={placeholder}
        className={styles.input}
      />
      
      {showSuggestions && suggestions && suggestions.length > 0 && (
        <ul className={styles.suggestions}>
          {suggestions.map((suggestion, i) => (
            <li
              key={i}
              className={styles.suggestion}
              onClick={() => handleSelect(suggestion)}
            >
              <span className={styles.suggestionMain}>
                {suggestion.displayName}
              </span>
              {suggestion.district && (
                <span className={styles.suggestionSub}>
                  {suggestion.district}, {suggestion.city}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      
      {showSuggestions && isLoading && (
        <div className={styles.loading}>Searching...</div>
      )}
    </div>
  )
}
